/**
 * Quotes Pipeline Page
 *
 * Read-only kanban dashboard for quote status, value, PO references, and
 * lifecycle dates.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertCircle,
  Building2,
  CalendarDays,
  DollarSign,
  FileText,
  Search,
  X,
} from 'lucide-react';
import { cn, formatCompactCurrency, formatCurrency, formatDate } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { financialKeys, useQuotesPipelineSummary, useQuotesPipeline } from '../hooks/useFinancialMetrics';
import { QuoteFormModal } from '@/features/quotes/components/modals';
import {
  useQuoteV3,
  useQuoteV4,
  useEnsureQuoteMongo,
  useCreateQuote,
  useUpdateQuote,
} from '@/features/quotes/hooks';
import type { CreateQuoteInput, UpdateQuoteInput, V3Quote } from '@/features/quotes/types';
import type { QuotesPipelineQueryParams, QuotePipelineBucket, QuotePipelineItem } from '../types';

import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

const PAGE_SIZE = 100;

const STATUS_LABELS: Record<string, string> = {
  preparation: 'Preparation',
  sent: 'Sent',
  expired: 'Expired',
  approved: 'Approved',
  inprogress: 'In Progress',
  'invoice-ready': 'Ready to Invoice',
  invoiced: 'Invoiced',
  'partially-invoiced': 'Partially Invoiced',
  'waiting-po': 'Waiting PO',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

const STATUS_COLORS: Record<string, string> = {
  preparation: '#6b7280',
  sent: '#3b82f6',
  expired: '#f97316',
  approved: '#8b5cf6',
  inprogress: '#f59e0b',
  'invoice-ready': '#06b6d4',
  invoiced: '#22c55e',
  'partially-invoiced': '#84cc16',
  'waiting-po': '#6366f1',
  cancelled: '#64748b',
  rejected: '#ef4444',
};

const STATUS_BADGE_STYLES: Record<string, string> = {
  preparation: 'bg-gray-100 text-gray-800 border-gray-200',
  sent: 'bg-blue-100 text-blue-800 border-blue-200',
  expired: 'bg-orange-100 text-orange-800 border-orange-200',
  approved: 'bg-violet-100 text-violet-800 border-violet-200',
  inprogress: 'bg-amber-100 text-amber-800 border-amber-200',
  'invoice-ready': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  invoiced: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'partially-invoiced': 'bg-lime-100 text-lime-800 border-lime-200',
  'waiting-po': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  cancelled: 'bg-slate-100 text-slate-800 border-slate-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

const STATUS_ORDER = [
  'preparation',
  'sent',
  'expired',
  'approved',
  'inprogress',
  'invoice-ready',
  'partially-invoiced',
  'invoiced',
  'waiting-po',
  'rejected',
  'cancelled',
];

interface QuoteKanbanColumn {
  status: string;
  label: string;
  count: number;
  total: number;
  color: string;
  quotes: QuotePipelineItem[];
}

function buildBucketMap(
  buckets: QuotePipelineBucket[] | undefined,
  quotes: QuotePipelineItem[],
): Map<string, QuotePipelineBucket> {
  const bucketMap = new Map<string, QuotePipelineBucket>();

  for (const bucket of buckets ?? []) {
    bucketMap.set(bucket.status, bucket);
  }

  for (const quote of quotes) {
    if (!bucketMap.has(quote.status)) {
      bucketMap.set(quote.status, {
        status: quote.status,
        label: STATUS_LABELS[quote.status] || quote.status,
        count: 0,
        total: 0,
        color: STATUS_COLORS[quote.status] || '#6b7280',
      });
    }
  }

  return bucketMap;
}

function quoteMatchesSearch(quote: QuotePipelineItem, query: string): boolean {
  if (!query) return true;

  return [
    quote.quote_number,
    quote.po_number,
    quote.account_name,
    quote.account_number,
    STATUS_LABELS[quote.status] || quote.status,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
}

function QuoteKanbanCard({
  quote,
  highlighted,
  onClick,
}: {
  quote: QuotePipelineItem;
  highlighted: boolean;
  onClick: () => void;
}) {
  const cardRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (highlighted) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [highlighted]);

  return (
    <button
      ref={cardRef}
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border bg-card p-3 text-left shadow-sm transition hover:shadow-md',
        highlighted && 'ring-2 ring-yellow-300 bg-yellow-50',
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="font-mono text-sm font-semibold">#{quote.quote_number}</span>
        <Badge
          className={cn(
            'shrink-0 text-[10px]',
            STATUS_BADGE_STYLES[quote.status] || 'bg-gray-100 text-gray-800 border-gray-200',
          )}
        >
          {STATUS_LABELS[quote.status] || quote.status}
        </Badge>
      </div>

      <div className="space-y-1.5 text-xs">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Building2 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{quote.account_name || 'No account'}</span>
        </div>

        <div className="flex items-center gap-3 text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1.5">
            <FileText className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{quote.po_number || 'No PO'}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <DollarSign className="h-3.5 w-3.5 shrink-0" />
            <span>{formatCurrency(quote.total, quote.currency)}</span>
          </span>
        </div>

        <div className="flex items-center gap-1.5 text-muted-foreground">
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />
          <span>Created {formatDate(quote.created_date)}</span>
        </div>

        {quote.estimated_delivery && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            <span>Est. delivery {formatDate(quote.estimated_delivery)}</span>
          </div>
        )}
      </div>
    </button>
  );
}

function QuoteKanbanColumn({
  column,
  highlightId,
  onQuoteClick,
}: {
  column: QuoteKanbanColumn;
  highlightId: string | null;
  onQuoteClick: (quote: QuotePipelineItem) => void;
}) {
  return (
    <section
      aria-label={`${column.label} quotes`}
      className="flex min-w-[280px] w-[280px] shrink-0 flex-col rounded-lg border bg-muted/30"
    >
      <div className="space-y-2 border-b p-3" style={{ borderTop: `3px solid ${column.color}` }}>
        <div className="flex items-center justify-between gap-2">
          <h2 className="truncate text-sm font-semibold">{column.label}</h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {column.count}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">
            <FileText className="h-3 w-3" />
            <span>{column.count} quotes</span>
          </div>
          <div className="flex items-center gap-1 rounded bg-green-50 px-1.5 py-0.5 text-xs text-green-700">
            <DollarSign className="h-3 w-3" />
            <span>{formatCompactCurrency(column.total)}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto p-2" style={{ minHeight: 'calc(100vh - 350px)' }}>
        {column.quotes.length > 0 ? (
          column.quotes.map((quote) => (
            <QuoteKanbanCard
              key={quote.id}
              quote={quote}
              highlighted={highlightId === quote.quote_number}
              onClick={() => onQuoteClick(quote)}
            />
          ))
        ) : (
          <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
            No quotes
          </div>
        )}
      </div>
    </section>
  );
}

function QuoteKanbanSkeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={`quote-kanban-skeleton-${index}`}
          className="flex min-w-[280px] w-[280px] shrink-0 flex-col rounded-lg border bg-muted/30"
        >
          <div className="space-y-3 border-b p-3">
            <Skeleton className="h-4 w-36" />
            <div className="flex gap-3">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-5 w-20" />
            </div>
          </div>
          <div className="space-y-2 p-2">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function QuotesPipelinePage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status');
  const highlightId = searchParams.get('highlight');

  const { t } = useTranslation('operations-financial');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);
  const [editingQuotePgId, setEditingQuotePgId] = useState<string | null>(null);
  const [editingQuoteContext, setEditingQuoteContext] = useState<QuotePipelineItem | null>(null);

  // #890 — date-range filter (created OR invoice date), applied to BOTH the list
  // and the summary so the board and its bucket counts reflect the same window.
  const [dateField, setDateField] = useState<'created' | 'invoiced'>('created');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const dateFilter = useMemo<Partial<QuotesPipelineQueryParams>>(() => {
    if (!dateFrom && !dateTo) return {};
    if (dateField === 'invoiced') {
      return {
        ...(dateFrom ? { invoiced_date_from: dateFrom } : {}),
        ...(dateTo ? { invoiced_date_to: dateTo } : {}),
      };
    }
    return {
      ...(dateFrom ? { created_date_from: dateFrom } : {}),
      ...(dateTo ? { created_date_to: dateTo } : {}),
    };
  }, [dateField, dateFrom, dateTo]);

  const hasDateFilter = Boolean(dateFrom || dateTo);

  const clearDateFilter = useCallback(() => {
    setDateFrom('');
    setDateTo('');
  }, []);

  const listParams: QuotesPipelineQueryParams = {
    status_in: statusFromUrl ? [statusFromUrl] : undefined,
    page_number: 0,
    page_length: PAGE_SIZE,
    sort_key: 'created_date',
    sort_direction: -1,
    ...dateFilter,
  };

  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuotesPipelineSummary(dateFilter);
  const { data: listData, isLoading: listLoading, error: listError } = useQuotesPipeline(listParams);
  const editQuoteV3 = useQuoteV3(editingQuoteId);
  const editQuoteV4 = useQuoteV4(editingQuotePgId);
  const createMutation = useCreateQuote();
  const updateMutation = useUpdateQuote();
  const ensureMongoMutation = useEnsureQuoteMongo();

  const quotes = useMemo(() => listData?.quotes ?? [], [listData?.quotes]);
  const total = listData?.total ?? 0;
  const query = debouncedSearch.trim().toLowerCase();
  const hasError = summaryError || listError;

  const columns = useMemo<QuoteKanbanColumn[]>(() => {
    const bucketMap = buildBucketMap(summaryData?.buckets, quotes);
    const visibleStatuses = statusFromUrl
      ? [statusFromUrl]
      : Array.from(bucketMap.keys()).sort((a, b) => {
          const aIndex = STATUS_ORDER.indexOf(a);
          const bIndex = STATUS_ORDER.indexOf(b);
          return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
        });

    return visibleStatuses.map((status) => {
      const bucket = bucketMap.get(status) ?? {
        status,
        label: STATUS_LABELS[status] || status,
        count: 0,
        total: 0,
        color: STATUS_COLORS[status] || '#6b7280',
      };
      const columnQuotes = quotes.filter(
        (quote) => quote.status === status && quoteMatchesSearch(quote, query),
      );
      const isSearchFiltered = query.length > 0;

      return {
        status,
        label: bucket.label || STATUS_LABELS[status] || status,
        count: isSearchFiltered ? columnQuotes.length : bucket.count,
        total: isSearchFiltered
          ? columnQuotes.reduce((sum, quote) => sum + quote.total, 0)
          : bucket.total,
        color: bucket.color || STATUS_COLORS[status] || '#6b7280',
        quotes: columnQuotes,
      };
    });
  }, [query, quotes, statusFromUrl, summaryData?.buckets]);

  const visibleQuoteCount = columns.reduce((sum, column) => sum + column.quotes.length, 0);

  const resetQuoteEditor = useCallback(() => {
    setIsFormOpen(false);
    setEditingQuoteId(null);
    setEditingQuotePgId(null);
    setEditingQuoteContext(null);
  }, []);

  useEffect(() => {
    if (!editingQuoteId && editQuoteV4.data?.mongoQuoteId) {
      setEditingQuoteId(editQuoteV4.data.mongoQuoteId);
    }
  }, [editingQuoteId, editQuoteV4.data?.mongoQuoteId]);

  useEffect(() => {
    if (!editingQuotePgId) return;

    if (editQuoteV4.isError) {
      toast.error('Failed to load quote for editing');
      resetQuoteEditor();
    } else if (editQuoteV4.isSuccess && !editQuoteV4.data) {
      toast.error('Quote not found in database');
      resetQuoteEditor();
    }
  }, [
    editQuoteV4.data,
    editQuoteV4.isError,
    editQuoteV4.isSuccess,
    editingQuotePgId,
    resetQuoteEditor,
  ]);

  const editingInitialCustomerData = useMemo(() => {
    const source = editingQuoteContext;
    const v4Quote = editQuoteV4.data;

    if (!source && !v4Quote) {
      return undefined;
    }

    return {
      accountName: source?.account_name || v4Quote?.accountName || undefined,
      accountNumber: source?.account_number || v4Quote?.accountNumber || undefined,
      contractId: v4Quote?.subscriptionId || undefined,
      salesManagerId: v4Quote?.salesManagerId || undefined,
      contractualManagerId: source?.contractual_manager_id || v4Quote?.contractualManagerId || undefined,
      pgAccountId: source?.account_id || v4Quote?.accountId || undefined,
      lockCustomer: true,
    };
  }, [editingQuoteContext, editQuoteV4.data]);

  const handleQuoteClick = useCallback((quote: QuotePipelineItem) => {
    setEditingQuoteId(null);
    setEditingQuotePgId(quote.id);
    setEditingQuoteContext(quote);
    setIsFormOpen(true);
  }, []);

  const handleFormSubmit = async (data: CreateQuoteInput | UpdateQuoteInput) => {
    let created: V3Quote | undefined;
    if (editingQuotePgId) {
      // Route updates through the PG UUID so V4 read paths (Kanban, Print,
      // lists) reflect the change immediately. updateQuote() detects the
      // UUID and PUTs /v4/quotes/:id; no Mongo backing doc is required.
      await updateMutation.mutateAsync({ id: editingQuotePgId, data: data as UpdateQuoteInput });
    } else {
      created = await createMutation.mutateAsync(data as CreateQuoteInput);
    }

    await queryClient.invalidateQueries({ queryKey: financialKeys.all });
    // Return the created quote so the form can attach sensors that were staged
    // before the quote (and its server-assigned number) existed.
    return created;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Quotes Pipeline</h1>
        <p className="text-sm text-muted-foreground">
          Quote status, value, PO references, and lifecycle dates
        </p>
      </div>

      {hasError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load quotes pipeline data. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search quote #, PO #, account, or status..."
            className="pl-9 pr-9"
          />
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={dateField} onValueChange={(value) => setDateField(value as 'created' | 'invoiced')}>
            <SelectTrigger className="h-9 w-[150px]" aria-label={t('pipeline.dateField', 'Date field')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created">{t('pipeline.createdDate', 'Created date')}</SelectItem>
              <SelectItem value="invoiced">{t('pipeline.invoiceDate', 'Invoice date')}</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(event) => setDateFrom(event.target.value)}
            className="h-9 w-[150px]"
            aria-label={t('pipeline.dateFrom', 'From date')}
          />
          <span className="text-sm text-muted-foreground">{t('pipeline.dateRangeTo', 'to')}</span>
          <Input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(event) => setDateTo(event.target.value)}
            className="h-9 w-[150px]"
            aria-label={t('pipeline.dateTo', 'To date')}
          />
          {hasDateFilter && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearDateFilter}
              aria-label={t('pipeline.clearDateFilter', 'Clear date filter')}
            >
              <X className="mr-1 h-4 w-4" />
              {t('pipeline.clearDates', 'Clear dates')}
            </Button>
          )}
        </div>

        <div className="text-sm text-muted-foreground">
          {query
            ? `Showing ${visibleQuoteCount} matching quotes`
            : `Showing ${Math.min(quotes.length, PAGE_SIZE)} of ${total}`}
        </div>
      </div>

      {summaryLoading || listLoading ? (
        <QuoteKanbanSkeleton />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map((column) => (
            <QuoteKanbanColumn
              key={column.status}
              column={column}
              highlightId={highlightId}
              onQuoteClick={handleQuoteClick}
            />
          ))}
        </div>
      )}

      <QuoteFormModal
        open={isFormOpen && (
          (!editingQuoteId && !editingQuotePgId)
          || (!editQuoteV4.isLoading && !!editQuoteV4.data)
        )}
        onOpenChange={(open) => {
          if (!open) {
            void queryClient.invalidateQueries({ queryKey: financialKeys.all });
            resetQuoteEditor();
          }
        }}
        quote={editQuoteV3.data ?? null}
        v4Quote={editQuoteV4.data ?? null}
        pgQuoteId={editingQuotePgId || undefined}
        initialCustomerData={editingQuotePgId ? editingInitialCustomerData : undefined}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending || ensureMongoMutation.isPending}
      />
    </div>
  );
}
